import { AuthenticationResult, EventType, PublicClientApplication } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import { loginRequest, msalConfig } from 'authConfig';
import { ALFAPASS_TOKEN_ENDPOINT } from 'config/environment';
import { ReactNode } from 'react';
import { NavigateFunction, useNavigate } from 'react-router-dom';

interface AuthProviderProps {
  children: ReactNode;
}
const msalInstance = new PublicClientApplication(msalConfig);

const AuthProvider = ({ children }: AuthProviderProps) => {
  const navigate = useNavigate();
  if (!msalInstance.getActiveAccount() && msalInstance.getAllAccounts().length > 0) {
    msalInstance.setActiveAccount(msalInstance.getAllAccounts()[0]);
  }

  msalInstance.addEventCallback((event) => {
    const authenticationResult = event.payload as AuthenticationResult;
    const account = authenticationResult?.account;
    if (event.eventType === EventType.LOGIN_SUCCESS && account) {
      msalInstance.setActiveAccount(account);
    }
    if (event.eventType === EventType.LOGIN_FAILURE && event.error) {
      handleLoginFailure(navigate, event.error);
    }
  });

  return <MsalProvider instance={msalInstance}>{children}</MsalProvider>;
};

const handleLoginFailure = (navigate: NavigateFunction, error: any) => {
  if (error.errorCode !== 'server_error') return;
  msalInstance.clearCache();
  navigate('/error/login');
};

export async function acquireAccessToken() {
  let activeAccount = msalInstance.getActiveAccount();
  const accounts = msalInstance.getAllAccounts();

  if (!activeAccount && accounts.length > 0) {
    msalInstance.setActiveAccount(accounts[0]);
    activeAccount = accounts[0];
  }

  if (!activeAccount) {
    console.error('No active account. User likely needs to login.');
    await msalInstance.loginRedirect(loginRequest);
    throw new Error('Redirecting to login');
  }

  try {
    const response = await msalInstance.acquireTokenSilent(loginRequest);
    return response.accessToken;
  } catch (error) {
    return msalInstance.acquireTokenRedirect(loginRequest);
  }
}

export async function logOut() {
  const alfapassToken = localStorage.getItem('alfapassToken');
  let redirect_uri: string | null = null;
  if (alfapassToken) {
    const authorizeEndpoint = ALFAPASS_TOKEN_ENDPOINT!;
    const alfapassLogoutUri = authorizeEndpoint.replace('token', 'logout');
    const postLogoutRedirectUri = window.location.origin;
    const obj = JSON.parse(alfapassToken);
    const idTokenHint = obj.id_token;
    redirect_uri = `${alfapassLogoutUri}?id_token_hint=${idTokenHint}&post_logout_redirect_uri=${postLogoutRedirectUri}`;
  }
  localStorage.removeItem('codeVerifier');
  localStorage.removeItem('alfapassToken');

  try {
    await msalInstance.logoutRedirect({
      postLogoutRedirectUri: window.location.origin,
    });
  } catch (error) {
    console.error('Logout failed', error);
  }
}

export function useAuthProvider() {
  return { AuthProvider };
}
